import React, { useCallback, useRef } from "react";
import { Configure, InstantSearch, Hits, Pagination, useSearchBox, useInstantSearch } from "react-instantsearch";
import AlgoliaPagination from "./AlgoliaPagination";
import {
  createQueryPageRouting,
  decodeEntities,
  trimExcerpt,
  useAlgoliaClient,
  useSearchInput,
} from "./utils";

const formatNumber = (num) =>
  typeof num === "number" ? num.toLocaleString() : num;

const HitCard = ({ hit }) => {
  const title = decodeEntities(
    hit?.headline || hit?.title || hit?.pageHeadline || "Untitled"
  );
  const url = hit?.url || "#";
  const excerpt =
    hit?.excerpt ||
    hit?.pageIntroText ||
    hit?.introText ||
    hit?.heroBodyText ||
    hit?.contentBuilderContent ||
    "";
  const imageUrl = hit?.imageUrl || null;
  const imageAlt = hit?.imageAltText || title || "Placeholder";
  const displayUrl =
    typeof url === "string" ? url.replace(/(^\w+:|^)\/\//, "") : "";
  const typeLabelMap = {
    press: "Press",
    blog: "Blog",
    servicesPages: "Service",
    ourDifference: "Our Difference",
    ourDifferencePages: "Our Difference",
  };
  const typeLabel = typeLabelMap[hit?.entryType] || null;

  return (
    <article className="b-searchable-global-list__hit">
      <a
        className="b-searchable-global-list__hit-link"
        href={url}
        aria-label={title}
      />
      {imageUrl ? (
        <a
          className="b-searchable-global-list__hit-image"
          href={url}
          title={title}
        >
          <img src={imageUrl} alt={imageAlt} loading="lazy" />
        </a>
      ) : (
        <div className="b-searchable-global-list__hit-image" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="56"
            height="56"
            viewBox="0 0 56 56"
            fill="none"
          >
            <path
              d="M10 0.5H46C51.2467 0.5 55.5 4.7533 55.5 10V46C55.5 51.2467 51.2467 55.5 46 55.5H10C4.75329 55.5 0.5 51.2467 0.5 46V10C0.5 4.75329 4.7533 0.5 10 0.5Z"
              stroke="#9D9D9D"
            />
            <line
              y1="-0.5"
              x2="16"
              y2="-0.5"
              transform="matrix(1 8.74228e-08 8.74228e-08 -1 20 27.5)"
              stroke="#9D9D9D"
            />
            <path
              d="M28.4706 36L36 28L28.4706 20"
              stroke="#9D9D9D"
            />
          </svg>
        </div>
      )}
      <div className="b-searchable-global-list__hit-body">
        {typeLabel ? (
          <div className="b-searchable-global-list__hit-type">{typeLabel}</div>
        ) : null}
        <h3>
          <a href={url} title={title}>
            {title}
          </a>
        </h3>
        {displayUrl ? (
          <div className="b-searchable-global-list__hit-url">
            <a href={url} title={displayUrl}>
              {displayUrl}
            </a>
          </div>
        ) : null}
        {excerpt ? <p>{trimExcerpt(excerpt, 360)} </p> : null}
      </div>
    </article>
  );
};

const CustomSearchBox = ({ onScroll }) => {
  const { value, onChange, onReset, onSubmit } = useSearchInput({
    onChange: () => onScroll?.(),
    onSubmit: () => onScroll?.(),
    onReset: () => onScroll?.(),
  });

  return (
    <div className="ais-SearchBox">
      <form
        className="ais-SearchBox-form"
        role="search"
        noValidate
        onSubmit={onSubmit}
        onReset={onReset}
      >
        <input
          className="ais-SearchBox-input"
          type="search"
          value={value}
          onChange={onChange}
          placeholder="Search site content…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        <span className="ais-SearchBox-magnifier" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M16.9 18L10.325 11.425C9.825 11.8583 9.24201 12.1958 8.57603 12.4375C7.91004 12.6792 7.20137 12.8 6.45 12.8C4.6473 12.8 3.12163 12.175 1.87298 10.925C0.624325 9.675 0 8.16667 0 6.4C0 4.63333 0.625 3.125 1.875 1.875C3.125 0.625 4.6375 0 6.4125 0C8.1875 0 9.69583 0.625 10.9375 1.875C12.1792 3.125 12.8 4.63458 12.8 6.40375C12.8 7.11792 12.6833 7.80833 12.45 8.475C12.2167 9.14167 11.8667 9.76667 11.4 10.35L18 16.9L16.9 18ZM6.425 11.3C7.77917 11.3 8.93021 10.8208 9.87813 9.8625C10.826 8.90417 11.3 7.75 11.3 6.4C11.3 5.05 10.826 3.89583 9.87813 2.9375C8.93021 1.97917 7.77917 1.5 6.425 1.5C5.05695 1.5 3.8941 1.97917 2.93645 2.9375C1.97882 3.89583 1.5 5.05 1.5 6.4C1.5 7.75 1.97882 8.90417 2.93645 9.8625C3.8941 10.8208 5.05695 11.3 6.425 11.3Z"
              fill="currentColor"
            />
          </svg>
        </span>
        {value ? (
          <button
            type="reset"
            className="ais-SearchBox-reset"
            aria-label="Clear search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
            >
              <line
                x1="1.11922"
                y1="0.706956"
                x2="14.5542"
                y2="14.142"
                stroke="currentColor"
                strokeWidth="2"
              />
              <line
                y1="-1"
                x2="19"
                y2="-1"
                transform="matrix(-0.707107 0.707107 0.707107 0.707107 14.8496 1.41406)"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
        ) : null}
        <button
          className="ais-SearchBox-submit"
          type="submit"
          aria-label="Submit search"
        >
          Search
        </button>
      </form>
    </div>
  );
};


function SearchableGlobalList({
  appId: propAppId,
  searchKey: propSearchKey,
  indexName: propIndexName,
  hitsPerPage = 16,
  heading = null,
} = {}) {
  const { client: searchClient, appId, searchKey } = useAlgoliaClient(
    propAppId,
    propSearchKey
  );
  const indexName = propIndexName ?? "global";

  const containerRef = useRef(null);
  const anchorRef = useRef(null);

  const scrollToList = useCallback(() => {
    if (typeof window === "undefined") return;
    const el = anchorRef.current || containerRef.current;
    if (!el) return;
    window.requestAnimationFrame(() => {
      const top = el.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top,
        behavior: "smooth",
      });
    });
  }, []);


  if (!appId || !searchKey || !indexName || !searchClient) {
    return null;
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      routing={createQueryPageRouting({ indexName })}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure hitsPerPage={hitsPerPage} />
      <div style={{ display: "none" }} aria-hidden="true">
        <Pagination />
      </div>
      <div className="b-searchable-global-list__inner" ref={containerRef}>
        <div
          className="b-searchable-global-list__anchor"
          ref={anchorRef}
          aria-hidden="true"
        />
        <div className="b-searchable-global-list__header">
          <div className="b-searchable-global-list__header-main">
            <h1>
              <HeaderTitle heading={heading} />
            </h1>
            <div className="b-searchable-global-list__search">
              <CustomSearchBox onScroll={scrollToList} />
            </div>
          </div>
        </div>

        <div className="b-searchable-global-list__results">
          <ResultsSection onScroll={scrollToList} />
        </div>
      </div>
    </InstantSearch>
  );
}

export default SearchableGlobalList;

const allowUnderlineOnly = (html) => {
  if (!html || typeof html !== "string") return "";
  // strip all tags except <u> and </u>
  return html.replace(/<(?!\/?u\b)[^>]*>/gi, "");
};

const HeaderTitle = ({ heading }) => {
  const { results } = useInstantSearch();
  const { query } = useSearchBox();
  const hasQuery = !!(query && query.trim().length);
  const total = results?.nbHits ?? 0;
  const resultWord =
    total === 1 ? "result" : "results";
  if (!hasQuery) {
    const safeHeading = heading ? allowUnderlineOnly(heading) : "Search";
    return <span dangerouslySetInnerHTML={{ __html: safeHeading }} />;
  }
  return (
    <>
      {formatNumber(total)}{" "}
      <span className="text-blue-400">{resultWord}</span>
      {` for “${query}”`}
    </>
  );
};

const ResultsSection = ({ onScroll }) => {
  const { results, setIndexUiState } = useInstantSearch();
  const { query, refine: setQuery } = useSearchBox();
  const hasQuery = !!(query && query.trim().length);
  const hasResults = results && results.nbHits > 0;
  const clearAll = useCallback(() => {
    setQuery("");
    setIndexUiState((ui) => ({
      ...ui,
      query: "",
      page: 1,
    }));
    onScroll?.();
  }, [onScroll, setIndexUiState, setQuery]);

  if (!hasQuery) {
    return (
      <div className="b-searchable-global-list__no-results">
        <p>Start by entering keywords or phrases in the search bar above.</p>
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className="b-searchable-global-list__no-results">
        <p>
          No results match your search.{" "}
          <button
            type="button"
            className="b-searchable-global-list__clear-link"
            onClick={clearAll}
          >
            Clear search
          </button>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="b-searchable-global-list__hits">
        <Hits hitComponent={HitCard} />
      </div>
      <AlgoliaPagination
        onScroll={onScroll}
      />
    </>
  );
};
